import { motion, AnimatePresence } from 'framer-motion'
import type { Adventure, Choice, QuizConfig, StorySegment } from '../../types'
import { useAdventure } from '../../hooks/useAdventure'
import Tilly from '../Tilly/Tilly'
import CountAndTap from '../Quiz/CountAndTap'
import SimpleAddition from '../Quiz/SimpleAddition'
import NumberRecognition from '../Quiz/NumberRecognition'
import PatternComplete from '../Quiz/PatternComplete'
import { Sparkles, ChevronRight, Star } from 'lucide-react'

interface AdventureEngineProps {
  adventure: Adventure
  onComplete: (score: number, timeSpent: number) => void
  onExit: () => void
}

export default function AdventureEngine({ adventure, onComplete, onExit }: AdventureEngineProps) {
  const {
    currentState,
    currentSegment,
    progress,
    tillyLine,
    tillyExpression,
    isTillySpeaking,
    handleChoice,
    handleQuizComplete,
    handleNext,
    isComplete,
    isChallengeMode,
    challengeQuizIndex,
    totalChallengeQuizzes,
    quizResults
  } = useAdventure(adventure)

  // Calculate score
  const correctAnswers = quizResults.filter(r => r).length
  const totalAnswered = quizResults.length
  const score = totalAnswered > 0 ? Math.round((correctAnswers / totalAnswered) * 100) : 0

  // Handle adventure completion
  if (isComplete) {
    return (
      <motion.div 
        className="h-[100dvh] flex flex-col items-center justify-center p-6 bg-gradient-to-br from-[var(--math-accent)]/20 to-[var(--math-success)]/20"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", duration: 0.8 }}
          className="text-8xl mb-6"
        >
          🏆
        </motion.div>
        
        <motion.h2 
          className="title text-3xl text-center mb-4"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          Adventure Complete!
        </motion.h2>
        
        <motion.div 
          className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 w-full max-w-sm mb-6"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <div className="text-center mb-3">
            <span className="text-5xl">{adventure.segments.reward.badgeIcon}</span>
            <p className="font-bold text-base mt-1">{adventure.segments.reward.badgeName}</p>
          </div>
          
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Score</span>
              <span className="font-bold text-[var(--math-success)]">{score}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Correct</span>
              <span className="font-bold">{correctAnswers}/{totalAnswered}</span>
            </div>
          </div>
        </motion.div>
        
        <motion.button
          onClick={() => onComplete(score, 0)}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className="btn-primary flex items-center gap-2 py-2.5 px-5 text-base"
          initial={{ y: 15, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <Sparkles className="w-4 h-4" />
          Continue
        </motion.button>
      </motion.div>
    )
  }

  // Render based on current state
  const renderContent = () => {
    switch (currentState) {
      case 'intro':
      case 'story-1':
      case 'story-2':
      case 'challenge':
        const segment = currentSegment as StorySegment
        return (
          <StoryView 
            onNext={handleNext}
            background={segment.background}
          />
        )

      case 'choice-1':
      case 'choice-2':
        const choices = currentSegment as Choice[]
        return (
          <ChoiceView 
            choices={choices} 
            onSelect={handleChoice}
          />
        )

      case 'quiz-1':
      case 'quiz-2':
      case 'quiz-sequence':
        const quiz = currentSegment as QuizConfig
        return (
          <QuizView 
            quiz={quiz} 
            onComplete={handleQuizComplete}
            isChallenge={isChallengeMode}
            challengeProgress={isChallengeMode ? `${challengeQuizIndex + 1}/${totalChallengeQuizzes}` : undefined}
          />
        )

      case 'reward':
        const reward = currentSegment as { tillyLine: string; badgeName: string; badgeIcon: string }
        return (
          <RewardView 
            reward={reward}
            onNext={handleNext}
          />
        )

      default:
        return null
    }
  }

  return (
    <div className="h-full flex flex-col bg-[var(--math-bg)]">
      {/* Progress Bar */}
      <div className="px-5 pt-4 pb-2">
        <div className="flex items-center justify-between mb-2">
          <button 
            onClick={onExit}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            ✕ Exit
          </button>
          <span className="text-sm font-medium text-gray-600">{progress}%</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <motion.div 
            className="h-full bg-[var(--math-primary)] rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
      </div>

      {/* Main Content Area - No scroll, fit to screen */}
      <div className="flex-1 overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentState + (isChallengeMode ? challengeQuizIndex : '')}
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="h-full"
          >
            {renderContent()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Tilly at Bottom - Compact */}
      <div className="px-3 pb-3">
        <div className="bg-white rounded-2xl p-3 shadow-md flex items-center gap-3">
          <Tilly 
            expression={tillyExpression}
            isSpeaking={isTillySpeaking}
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm sm:text-base text-gray-800 leading-snug line-clamp-3">
              {tillyLine}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

// Story View Component
function StoryView({ onNext, background }: { 
  onNext: () => void
  background?: string 
}) {
  const bgGradients: Record<string, string> = {
    'orchard': 'from-orange-100 via-orange-50 to-green-50',
    'night-sky': 'from-indigo-900 via-purple-900 to-pink-800',
    'meadow': 'from-green-100 via-emerald-50 to-teal-50',
    'number-world': 'from-blue-100 via-indigo-50 to-purple-50',
    'math-forest': 'from-green-200 via-emerald-100 to-teal-50',
    'pattern-world': 'from-pink-100 via-rose-50 to-orange-50'
  }

  return (
    <div className={`h-full flex flex-col items-center justify-center p-4 bg-gradient-to-br ${bgGradients[background || 'orchard']}`}>
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="text-6xl mb-6"
      >
        {/* Dynamic scene emoji based on background */}
        {background === 'orchard' && '🍎'}
        {background === 'night-sky' && '⭐'}
        {background === 'meadow' && '🦋'}
        {background === 'number-world' && '🔢'}
        {background === 'math-forest' && '🌲'}
        {background === 'pattern-world' && '🧩'}
        {!background && '🌟'}
      </motion.div>
      
      <motion.button
        onClick={onNext}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        className="btn-primary flex items-center gap-2 py-2.5 px-5 text-base"
        initial={{ y: 15, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.2 }}
      >
        Continue
        <ChevronRight className="w-4 h-4" />
      </motion.button>
    </div>
  )
}

// Choice View Component
function ChoiceView({ choices, onSelect }: { 
  choices: Choice[]
  onSelect: (choice: Choice) => void 
}) {
  return (
    <div className="h-full flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-3">
        {choices.map((choice, index) => (
          <motion.button
            key={choice.id}
            onClick={() => onSelect(choice)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05, duration: 0.2 }}
            className="w-full py-5 px-6 bg-white rounded-2xl shadow-sm border-2 border-gray-100 text-lg font-bold text-gray-800 hover:border-[var(--math-primary)] hover:shadow-md transition-all"
          >
            {choice.label}
          </motion.button>
        ))}
      </div>
    </div>
  )
}

// Quiz View Component
function QuizView({ 
  quiz, 
  onComplete, 
  isChallenge, 
  challengeProgress 
}: { 
  quiz: QuizConfig
  onComplete: (correct: boolean) => void
  isChallenge?: boolean
  challengeProgress?: string
}) {
  const renderQuiz = () => {
    switch (quiz.type) {
      case 'count-and-tap':
        return (
          <CountAndTap
            targetNumber={quiz.targetNumber || 3}
            objectEmoji={quiz.objectEmoji}
            onComplete={onComplete}
          />
        )
      
      case 'simple-addition':
        return (
          <SimpleAddition
            num1={quiz.num1 || 1}
            num2={quiz.num2 || 1}
            onComplete={onComplete}
          />
        )
      
      case 'number-recognition':
        return (
          <NumberRecognition
            targetNumber={quiz.targetNumber || 1}
            onComplete={onComplete}
          />
        )
      
      case 'pattern-complete':
        return (
          <PatternComplete
            pattern={quiz.pattern || [1, 2, '___']}
            options={quiz.options.map(o => parseInt(o.label || '0')).filter(n => !isNaN(n) && n > 0)}
            onComplete={onComplete}
          />
        )
      
      default:
        return (
          <div className="text-center p-8">
            <p>Quiz type: {quiz.type}</p>
          </div>
        )
    }
  }

  return (
    <div className="h-full flex flex-col">
      {/* Challenge indicator - Compact */}
      {isChallenge && challengeProgress && (
        <div className="px-4 pt-3">
          <div className="flex items-center justify-center gap-2 bg-[var(--math-accent)]/20 rounded-full py-1.5 px-3">
            <Star className="w-4 h-4 text-[var(--math-accent)]" />
            <span className="text-sm font-bold text-gray-700">
              Challenge {challengeProgress}
            </span>
          </div>
        </div>
      )}
      
      {/* Quiz content - Centered, no scroll */}
      <div className="flex-1 flex items-center justify-center p-3">
        {renderQuiz()}
      </div>
    </div>
  )
}

// Reward View Component
function RewardView({ reward, onNext }: { 
  reward: { tillyLine: string; badgeName: string; badgeIcon: string }
  onNext: () => void 
}) {
  return (
    <div className="h-full flex flex-col items-center justify-center p-4 bg-gradient-to-br from-[var(--math-success)]/10 to-[var(--math-accent)]/10">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", duration: 0.5 }}
        className="text-6xl mb-4"
      >
        {reward.badgeIcon}
      </motion.div>
      
      <motion.div
        initial={{ y: 15, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-center mb-6"
      >
        <h2 className="title text-xl mb-1">You earned the</h2>
        <p className="display text-2xl text-[var(--math-primary)]">{reward.badgeName}!</p>
      </motion.div>
      
      <motion.button
        onClick={onNext}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        className="btn-primary flex items-center gap-2 py-2.5 px-5 text-base"
        initial={{ y: 15, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.2 }}
      >
        <Sparkles className="w-4 h-4" />
        Claim Badge
      </motion.button>
    </div>
  )
}
